import { Search } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { ColDef, GetRowIdParams } from 'ag-grid-community';
import { useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BudgetRequestListItemDto, UserDropdownDto } from '../../../types';
import { useBudgetStatuses } from '../../../hooks/useBudgetStatuses';
import { useBrgList } from '../../../hooks/useBrgList';
import { usersApi } from '../../../api/users';
import type { MdmBudgetStatusDto } from '../../../api/budgetStatus';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface RecentBudgetActivityTableProps {
  budgets: BudgetRequestListItemDto[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onViewRequest: (request: BudgetRequestListItemDto) => void;
  onEditRequest: (request: BudgetRequestListItemDto) => void;
  onDeleteRequest: (request: BudgetRequestListItemDto) => void;
  onViewAll: () => void;
  isAdmin: boolean;
  highlightedBudgetId?: number | null;
  onHighlightClear?: () => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const formatted = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return formatted.replace(', ', ' ');
}

function createStatusBadgeCellRenderer(statuses: MdmBudgetStatusDto[]) {
  return function StatusBadgeCellRenderer(props: any) {
    const statusId = props.value as number;
    
    // Find the status by ID
    const statusData = statuses.find((s) => s.budgetStatusId === statusId);
    const statusName = statusData?.budgetStatusName || 'Unknown';
    
    // Use database colors if found, otherwise fallback to default
    const bg = statusData?.backgroundColor || '#fff3cd';
    const text = statusData?.textColor || '#856404';

    return (
      <span 
        style={{
          backgroundColor: bg,
          color: text,
          padding: '6px 12px',
          borderRadius: '10px',
          fontSize: '12px',
          fontWeight: '700',
          display: 'inline-block',
          whiteSpace: 'nowrap',
          textAlign: 'center'
        }}
      >
        {statusName}
      </span>
    );
  };
}

export function RecentBudgetActivityTable({
  budgets,
  searchQuery,
  onSearchChange,
  onViewRequest,
  onEditRequest,
  onDeleteRequest,
  onViewAll,
  isAdmin,
  highlightedBudgetId,
  onHighlightClear,
}: RecentBudgetActivityTableProps) {
  const gridRef = useRef<AgGridReact>(null);
  // Fetch all users (including inactive) to ensure we can resolve all requesters
  const { data: users = [] } = useQuery<UserDropdownDto[], Error>({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.list(undefined, false),
  });
  const { data: budgetStatuses = [] } = useBudgetStatuses();
  const { data: brgs = [] } = useBrgList();
  
  // Helper function to get user display name from userId
  const getUserName = (userId?: number): string => {
    if (!userId) return '—';
    const user = users.find(u => u.userId === userId);
    // Return display name, or fall back to showing userId if not found
    return user?.displayName || `User #${userId}`;
  };
  
  // Scroll to and highlight the matching row when highlightedBudgetId changes
  useEffect(() => {
    if (highlightedBudgetId && gridRef.current) {
      setTimeout(() => {
        const api = gridRef.current?.api;
        if (api) {
          const rowNode = api.getRowNode(String(highlightedBudgetId));
          if (rowNode && rowNode.rowIndex !== null) {
            api.ensureIndexVisible(rowNode.rowIndex, 'middle');
          }
        }
      }, 100);
      
      // Clear highlight after 3 seconds
      const timeout = setTimeout(() => {
        onHighlightClear?.();
      }, 3000);
      
      return () => clearTimeout(timeout);
    }
  }, [highlightedBudgetId, onHighlightClear]);

  // Custom CSS for AG Grid - matching Recognition grid style
  const customGridStyles = `
    .ag-theme-quartz {
      --ag-header-background-color: #1a3bcc;
      --ag-header-foreground-color: #fff;
      --ag-header-cell-text-color: #fff;
      --ag-header-cell-padding: 10px 12px;
      --ag-header-cell-hover-background-color: #1530a8;
      --ag-header-cell-moving-background-color: #1530a8;
      --ag-font-size: 11px;
      --ag-cell-horizontal-padding: 12px;
      --ag-cell-vertical-padding: 10px;
      --ag-row-height: 36px;
      --ag-row-border-color: #f0f0f0;
      background-color: #ffffff;
      border: none;
      border-radius: 0;
    }
    
    .ag-theme-quartz .ag-header {
      font-weight: 700;
      font-size: 11px;
      height: 36px;
      background-color: #1a3bcc;
      border-radius: 0;
    }
    
    .ag-theme-quartz .ag-header-cell {
      background-color: #1a3bcc !important;
      color: #fff !important;
      border-right: 1px solid #1530a8;
      font-weight: 700;
    }
    
    .ag-theme-quartz .ag-header-cell:last-child {
      border-right: none;
    }
    
    .ag-theme-quartz .ag-row {
      border-bottom: 1px solid #f0f0f0;
      height: 36px;
      background-color: #ffffff;
      cursor: pointer;
    }
    
    .ag-theme-quartz .ag-row:last-child {
      border-bottom: none;
    }
    
    .ag-theme-quartz .ag-row:hover {
      background-color: #f7f9ff !important;
    }
    
    .ag-theme-quartz .ag-cell {
      font-size: 11px;
      color: #333;
      padding: 10px 12px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
    }
    
    .ag-theme-quartz .ag-root {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
      border-radius: 0;
    }
    
    .ag-theme-quartz .ag-root-wrapper {
      border: none;
      border-radius: 0;
    }
    
    .ag-theme-quartz .ag-center-cols-viewport {
      overflow-x: auto;
    }
    
    .ag-row.highlighted {
      background-color: #fffacd !important;
      animation: highlight-fade 5s ease-in-out;
    }
    
    @keyframes highlight-fade {
      0% {
        background-color: #fffacd !important;
        box-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
      }
      100% {
        background-color: #ffffff !important;
        box-shadow: none;
      }
    }
  `;

  const columnDefs: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [
      {
        field: 'purposeDescription',
        headerName: 'Item / Expense',
        flex: 1.2,
        minWidth: 200,
        cellRenderer: (params: any) => {
          const text = params.value || 'Untitled';
          // Detect recognition entries by checking if they end with " Recognition"
          const isRecognition = text.endsWith(' Recognition') || text.includes(' Recognition ');
          // Project-linked entries are auto-created from Project Tracking
          const isProject = !isRecognition && !!params.data?.projectId;
          
          // Split text to separate the badge from the main text
          let mainText = text;
          if (isRecognition) {
            // Remove " Recognition" from the end for display
            mainText = text.replace(/ Recognition$/, '').trim();
          }
          
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              <span
                style={{
                  color: '#333',
                  fontSize: '11px',
                  textAlign: 'left',
                  fontWeight: '500',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
                title={text}
              >
                {mainText}
              </span>
              {isRecognition && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    backgroundColor: '#f3e5f5',
                    color: '#6a1b9a',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '9px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.2px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                  title="Auto-created from Recognition Requests"
                >
                  ★ Recognition
                </span>
              )}
              {isProject && (
                <span
                  data-testid="project-pill"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    backgroundColor: '#e0f2f1',
                    color: '#00695c',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '9px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.2px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                  title="Auto-created from Project Tracking"
                >
                  Project
                </span>
              )}
            </div>
          );
        },
      },
      {
        field: 'brgId',
        headerName: 'BRG',
        width: 180,
        cellRenderer: (params: any) => {
          const brg = brgs.find((b: any) => b.brgId === params.value);
          const brgName = brg?.name || `BRG ${params.value}`;
          
          return (
            <span
              style={{
                color: '#1a3bcc',
                fontSize: '11px',
                fontWeight: '700',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
                width: '100%',
              }}
              title={brgName}
            >
              {brgName}
            </span>
          );
        },
      },
      {
        field: 'requestedDate',
        headerName: 'Date and Time',
        width: 160,
        valueFormatter: (params: any) => formatDate(params.value),
      },
      {
        field: 'requestedById',
        headerName: 'Requested By',
        width: 140,
        cellRenderer: (params: any) => {
          const userId = params.value;
          const userName = getUserName(userId);
          const isNotFound = userName.startsWith('User #');
          
          return (
            <span 
              style={{ 
                fontSize: '11px',
                color: isNotFound ? '#999' : '#333',
                fontStyle: isNotFound ? 'italic' : 'normal'
              }}
            >
              {userName}
            </span>
          );
        },
      },
      {
        field: 'budgetStatusId',
        headerName: 'Status',
        width: 140,
        cellRenderer: createStatusBadgeCellRenderer(budgetStatuses),
        sortable: true,
        cellStyle: { paddingRight: '16px' },
      },
      {
        field: 'requestAmount',
        headerName: 'Spend',
        width: 120,
        valueFormatter: (params: any) => formatCurrency(params.value),
        cellStyle: { textAlign: 'right', paddingRight: '12px' },
      },
    ];

    return cols;
  }, [isAdmin, onViewRequest, onEditRequest, onDeleteRequest, users, getUserName, budgetStatuses, brgs]);

  const filteredBudgets = useMemo(() => {
    if (!searchQuery.trim()) return budgets;
    const query = searchQuery.toLowerCase();
    
    return budgets.filter((b) => {
      // Build a searchable string with purpose and user name
      const userName = b.requestedById 
        ? users.find(u => u.userId === b.requestedById)?.displayName || '' 
        : '';
      const searchableText = `${b.purposeDescription || ''} ${userName}`.toLowerCase();
      return searchableText.includes(query);
    });
  }, [budgets, searchQuery, users]);

  const contextMenuItems = useMemo(() => ({
    isAdmin,
    onEditRequest,
    onDeleteRequest,
  }), [isAdmin, onEditRequest, onDeleteRequest]);

  const getRowId = (params: GetRowIdParams) => `${params.data.budgetRequestId}`;

  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      overflow: 'hidden',
      marginBottom: '0',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    }}>
      <style>{customGridStyles}</style>
      
      {/* Header with Search */}
      <div style={{
        padding: '16px 16px 12px 16px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#111', margin: 0, letterSpacing: '0.3px' }}>Recent Budget Activity</h2>
        <a 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            onViewAll();
          }}
          style={{ fontSize: '12px', color: '#4064e8', textDecoration: 'none', fontWeight: '600', cursor: 'pointer' }}>
          [View All]
        </a>
      </div>
      
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#fafbfc'
      }}>
        <div style={{ position: 'relative' }}>
          <Search style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', width: '14px', height: '14px', color: '#999' }} />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{
              width: '100%',
              paddingLeft: '36px',
              paddingRight: '12px',
              paddingTop: '10px',
              paddingBottom: '10px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '14px',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
              backgroundColor: '#fff'
            }}
          />
        </div>
      </div>

      {/* AG Grid Table */}
      <div style={{ height: '400px', overflow: 'hidden' }} className="ag-theme-quartz" id="recent-budget-activity-table">
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={filteredBudgets}
          context={contextMenuItems}
          getRowId={getRowId}
          domLayout="normal"
          defaultColDef={{
            resizable: false,
            sortable: false,
            filter: false,
          }}
          suppressPaginationPanel={false}
          paginationPageSize={10}
          suppressCellFocus={true}
          suppressMovableColumns={true}
          getRowStyle={(params) => {
            if (highlightedBudgetId && params.data.budgetRequestId === highlightedBudgetId) {
              return { 
                backgroundColor: '#fffacd' as any,
                animation: 'highlight-fade 5s ease-in-out' as any
              };
            }
            return undefined;
          }}
          onRowClicked={(params) => {
            onViewRequest(params.data);
          }}
        />
      </div>
    </div>
  );
}
